const actions = {
  /**
   * LogIn the account.
   *
   * @param commit
   * @param dispatch
   * @param $http
   * @param form
   * @returns {Promise<void>}
   */
  async logIn({ commit, dispatch, rootState: { $http } }, form) {
    const { data } = await $http.post('/auth/login', form);

    commit('setAccessToken', data.access_token);
    commit('setAccessTokenAsNotExpired');

    localStorage.setItem('auth.accessToken', data.access_token);

    await dispatch('loadUser');
  },

  /**
   * Register the account.
   *
   * @param $http
   * @param form
   * @returns {Promise<void>}
   */
  async register({ rootState: { $http } }, form) {
    await $http.post('/register', form);
  },

  /**
   * Forgot the password.
   *
   * @param $http
   * @param form
   * @returns {Promise<void>}
   */
  async forgotPassword({ rootState: { $http } }, form) {
    await $http.post('/password/email', form);
  },

  /**
   * Reset the password.
   *
   * @param $http
   * @param form
   * @returns {Promise<void>}
   */
  async resetPassword({ rootState: { $http } }, form) {
    await $http.post('/password/reset', form);
  },

  /**
   * Resend confirmation email.
   *
   * @param $http
   * @param form
   * @returns {Promise<void>}
   */
  async resendConfirmationEmail({ rootState: { $http } }, form) {
    await $http.post('/email/resend', form);
  },

  /**
   * Verify the email.
   *
   * @param $http
   * @param firstToken
   * @param secondToken
   * @param expires
   * @param signature
   * @returns {Promise<void>}
   */
  async verifyEmail(
    { rootState: { $http } },
    {
      firstToken,
      secondToken,
      expires,
      signature,
    },
  ) {
    await $http.get(`/email/verify/${firstToken}/${secondToken}?expires=${expires}&signature=${signature}`);
  },

  /**
   * Expire the access token.
   *
   * @param commit
   */
  expireAccessToken({ commit }) {
    commit('removeAccessToken');
    commit('setAccessTokenAsExpired');

    localStorage.removeItem('auth.accessToken');
  },

  /**
   * Load the authenticated user.
   *
   * @param commit
   * @param $http
   * @returns {Promise<void>}
   */
  async loadUser({ commit, rootState: { $http } }) {
    const { data } = await $http.get('/auth/me');

    commit('setUser', data);
  },

  /**
   * Logout the authenticated user.
   *
   * @param commit
   * @returns {Promise<void>}
   */
  logout({ commit }) {
    commit('setAccessToken', null);

    localStorage.removeItem('auth.accessToken');
  },

  /**
   * Fetch the tokens list.
   *
   * @param commit
   * @param $http
   * @returns {Promise<void>}
   */
  async fetchTokensList({ commit, rootState: { $http } }) {
    const { data } = await $http.get('/user/token');

    commit('setTokens', data);
  },

  /**
   * Update the personal information
   * from authenticated user.
   *
   * @param $http
   * @param form
   * @returns {Promise<void>}
   */
  async updatePersonalInformation({ rootState: { $http } }, form) {
    await $http.put('/auth/me', form);
  },

  /**
   * Update the password from authenticated
   * user.
   *
   * @param $http
   * @param form
   * @returns {Promise<void>}
   */
  async updatePassword({ rootState: { $http } }, form) {
    await $http.post('/user/password', form);
  },

  /**
   * Update the organization information from authenticated
   * user.
   *
   * @param $http
   * @param form
   * @returns {Promise<void>}
   */
  async updateOrganizationInformation({ rootState: { $http } }, form) {
    await $http.put('/auth/me/organization', form);
  },

  /**
   * Find invite data
   *
   * @param commit
   * @param $http
   * @param token
   * @returns {Promise<void>}
   */
  async findInvite({ commit, rootState: { $http } }, token) {
    const { data } = await $http.get(`/invite/${token}`);

    commit('setInvite', data);
  },

  /**
   * Create user using invite
   *
   * @param $http
   * @param token
   * @param form
   * @returns {Promise<void>}
   */
  async createUserByInvite({ rootState: { $http } }, form) {
    await $http.post(`/invite/${form.token}/user`, form);
  },

  /**
   * Create user using invite
   *
   * @param $http
   * @param token
   * @param form
   * @returns {Promise<void>}
   */
  async sendInvite({ rootState: { $http } }, form) {
    await $http.post('/invite', form);
  },


  /**
   * Fetch the users list.
   *
   * @param commit
   * @param $http
   * @returns {Promise<void>}
   */
  async fetchUsersList({ commit, rootState: { $http } }) {
    const { data } = await $http.get('/user');

    commit('setUsers', data);
  },

  async fetchInvitesList({ commit, rootState: { $http } }) {
    const { data } = await $http.get('/invite');

    commit('setInvites', data);
  },

  async deleteInvite({ rootState: { $http } }, id) {
    await $http.delete(`/invite/${id}`);
  },

  async changeRole({ rootState: { $http } }, { id, role }) {
    await $http.patch(`/user/${id}/role`, { role });
  },

  async toggleUser({ rootState: { $http } }, id) {
    await $http.patch(`/user/${id}/toggle`);
  },

  async registerFirebaseCloudMessagingToken({ rootState: { $http } }, token) {
    await $http.post('/auth/me/fcm', { token });
  },
};

export default actions;
